const DEFAULT_BASE_PATH = '/api';

const PLOT_ACTIONS = new Set(['plant', 'water', 'weeds', 'pests', 'harvest']);
const VISIT_ACTIONS = new Set(['water', 'weeds', 'pests', 'steal']);

const farmRoot = (basePath = DEFAULT_BASE_PATH) => `${String(basePath).replace(/\/$/, '')}/yingtao/farm`;

const validId = (value, label) => {
  const id = Number(value);
  if (!Number.isInteger(id) || id < 1) throw new TypeError(`${label} must be a positive integer.`);
  return id;
};

const cleanBody = (action, payload = {}) => {
  if (action === 'plant') {
    const cropKey = String(payload.cropKey || '').trim();
    if (!cropKey) throw new TypeError('cropKey is required for plant.');
    return { action, cropKey };
  }
  if (['water', 'weeds', 'pests'].includes(action)) {
    return { action: 'tend', careType: action };
  }
  return { action };
};

export const buildPlotAction = (basePath, plotNumber, action, payload = {}) => {
  const plot = validId(plotNumber, 'plotNumber');
  if (!PLOT_ACTIONS.has(action)) throw new TypeError(`Unsupported plot action: ${action}`);
  return {
    method: 'POST',
    url: `${farmRoot(basePath)}/plots/${plot}/actions`,
    body: cleanBody(action, payload),
  };
};

export const buildVisitAction = (basePath, userId, plotNumber, action, payload = {}) => {
  const user = validId(userId, 'userId');
  const plot = validId(plotNumber, 'plotNumber');
  if (!VISIT_ACTIONS.has(action)) throw new TypeError(`Unsupported visit action: ${action}`);
  return {
    method: 'POST',
    url: `${farmRoot(basePath)}/users/${user}/plots/${plot}/actions`,
    body: cleanBody(action, payload),
  };
};

const request = (app, requestSpec) => app.request({
  method: requestSpec.method,
  url: requestSpec.url,
  ...(requestSpec.body === undefined ? {} : { body: requestSpec.body }),
});

const queryString = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value) !== '') query.set(key, String(value));
  });
  const encoded = query.toString();
  return encoded ? `?${encoded}` : '';
};

export const loadMyFarm = (app, basePath = DEFAULT_BASE_PATH) => request(app, {
  method: 'GET',
  url: farmRoot(basePath),
});

export const loadStudents = (app, params = {}, basePath = DEFAULT_BASE_PATH) => request(app, {
  method: 'GET',
  url: `${farmRoot(basePath)}/students${queryString(params)}`,
});

export const loadStudentFarm = (app, userId, basePath = DEFAULT_BASE_PATH) => request(app, {
  method: 'GET',
  url: `${farmRoot(basePath)}/users/${validId(userId, 'userId')}`,
});

export const runPlotAction = (app, plotNumber, action, payload = {}, basePath = DEFAULT_BASE_PATH) => request(app, buildPlotAction(basePath, plotNumber, action, payload));

export const runVisitAction = (app, userId, plotNumber, action, payload = {}, basePath = DEFAULT_BASE_PATH) => request(app, buildVisitAction(basePath, userId, plotNumber, action, payload));

export const sellInventory = (app, cropKey, quantity, basePath = DEFAULT_BASE_PATH) => {
  const key = String(cropKey || '').trim();
  const amount = validId(quantity, 'quantity');
  if (!key) throw new TypeError('cropKey is required.');
  return request(app, {
    method: 'POST',
    url: `${farmRoot(basePath)}/inventory/${encodeURIComponent(key)}/sell`,
    body: { quantity: amount },
  });
};

export const expandFarm = (app, basePath = DEFAULT_BASE_PATH) => request(app, {
  method: 'POST',
  url: `${farmRoot(basePath)}/expand`,
  body: {},
});

export const loadLeaderboard = (app, params = {}, basePath = DEFAULT_BASE_PATH) => request(app, {
  method: 'GET',
  url: `${farmRoot(basePath)}/leaderboards${queryString(params)}`,
});

export const loadActivity = (app, params = {}, basePath = DEFAULT_BASE_PATH) => request(app, {
  method: 'GET',
  url: `${farmRoot(basePath)}/activity${queryString(params)}`,
});
